import { useMemo, useState } from 'react';

import { AxiosError } from 'axios';

import { Card, Stack } from '@cast/design-system';
import { convertUnit } from '@cast/utils';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import { attachTimeTicks } from 'utils/chart';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { ChartCard, ChartCardLabel } from './ChartCard';
import { useNodeStorageTimeRangeSelection } from '../_hooks/useNodeStorageTimeRangeSelection';

type StorageDiskChartsProps = {
  series: {
    bucketTime?: string;
    iops?: number | string;
    provisionedIops?: number | string;
    provisionedThroughputBytes?: number | string;
    throughputBytes?: number | string;
    totalBytes?: number | string;
    usedBytes?: number | string;
  }[];
  summary?: {
    usedBytes?: number;
    provisionedSizeBytes?: number;
    throughputBytes?: number;
    iops?: number;
    provisionedIops?: number;
    provisionedThroughputBytes?: number;
  };
  isLoading: boolean;
  error: AxiosError | null;
  refetch: () => void;
  syncId: string;
  timeZone: string;
  isNodeReady: boolean;
  labels?: {
    capacity?: ChartCardLabel;
    throughput?: ChartCardLabel;
    iops?: ChartCardLabel;
  };
  showCapacity?: boolean;
  isCloudDisksEnabled?: boolean;
};

export const StorageDiskCharts = ({
  series,
  summary,
  isLoading,
  error,
  refetch,
  syncId,
  timeZone,
  isNodeReady,
  labels,
  showCapacity = true,
  isCloudDisksEnabled,
}: StorageDiskChartsProps) => {
  const { cluster } = useCluster();
  const [enabledMetrics, setEnabledMetrics] = useState({
    used: true,
    provisioned: true,
  });

  const { getRange } = useNodeStorageTimeRangeSelection();

  const analyzeCapacityWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'storage capacity usage',
      scopeName: cluster.name,
      getDefaultRange: getRange,
    }),
  });

  const analyzeIopsWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'storage IOPS usage',
      scopeName: cluster.name,
      getDefaultRange: getRange,
    }),
  });

  const analyzeThroughputWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'storage throughput usage',
      scopeName: cluster.name,
      getDefaultRange: getRange,
    }),
  });

  const chartData = useMemo(
    () =>
      attachTimeTicks(
        series
          .filter((s) => !!s.bucketTime)
          .map((s) => {
            const coreData = {
              timestamp: s.bucketTime!,
              usedBytes: s.usedBytes
                ? convertUnit(Number(s.usedBytes), 'Bytes', 'GiB')
                : 0,
              iops: s.iops ? Number(s.iops) : 0,
              throughputMiBs: s.throughputBytes
                ? convertUnit(Number(s.throughputBytes), 'Bytes', 'MiB')
                : 0,
            };

            if (isCloudDisksEnabled) {
              return {
                ...coreData,
                totalBytes: s.totalBytes
                  ? convertUnit(Number(s.totalBytes), 'Bytes', 'GiB')
                  : 0,

                provisionedIops: s.provisionedIops
                  ? Number(s.provisionedIops)
                  : 0,

                provisionedThroughputMiBs: s.provisionedThroughputBytes
                  ? convertUnit(
                      Number(s.provisionedThroughputBytes),
                      'Bytes',
                      'MiB'
                    )
                  : 0,
              };
            }

            return coreData;
          })
      ),
    [series, isCloudDisksEnabled]
  );

  const hasError = !!error;
  const sharedChartProps = {
    data: chartData,
    isLoading: isLoading || !isNodeReady,
    hasError,
    hasNoData: !chartData.length && !hasError,
    syncId,
    timeZone,
    refetch,
    isCloudDisksEnabled,
    zoomProps: { xAxisDataKey: 'chartTimestampUnix' },
  };

  if (error) {
    return (
      <Card sx={{ height: '338px' }}>
        <FailedToLoad
          title={error.message ?? 'Failed to load storage metrics'}
          sx={{ height: '100%', justifyContent: 'center' }}
          refresh={refetch}
        />
      </Card>
    );
  }

  return (
    <Stack direction="column" gap={16}>
      {showCapacity && (
        <ChartCard
          {...sharedChartProps}
          testId="capacity-chart"
          title="Capacity"
          tooltip="Provisioned disk capacity compared to actual usage over time."
          unit="GiB"
          metrics={{
            used: {
              avg:
                summary?.usedBytes !== undefined
                  ? convertUnit(summary.usedBytes, 'Bytes', 'GiB')
                  : undefined,
              dataKey: 'usedBytes',
            },
            provisioned: {
              avg:
                summary?.provisionedSizeBytes !== undefined
                  ? convertUnit(summary.provisionedSizeBytes, 'Bytes', 'GiB')
                  : undefined,
              dataKey: 'totalBytes',
              metricsBoxTooltip:
                'Filesystem-reported capacity. May be lower than cloud provider figures due to filesystem overhead (metadata & reserved blocks).',
            },
          }}
          labelConfig={labels?.capacity}
          enabledMetrics={enabledMetrics}
          onMetricChange={setEnabledMetrics}
          analyzeWithOpsPilot={analyzeCapacityWithOpsPilot}
        />
      )}
      <ChartCard
        {...sharedChartProps}
        title="IOPS"
        tooltip="Provisioned Input/Output Operations (IOPS) per second compared to actual usage over time."
        testId="iops-chart"
        unit="IOPS"
        metrics={{
          used: { avg: summary?.iops, dataKey: 'iops' },
          provisioned: {
            avg: summary?.provisionedIops,
            dataKey: 'provisionedIops',
          },
        }}
        labelConfig={labels?.iops}
        enabledMetrics={enabledMetrics}
        onMetricChange={setEnabledMetrics}
        analyzeWithOpsPilot={analyzeIopsWithOpsPilot}
      />
      <ChartCard
        {...sharedChartProps}
        title="Throughput"
        tooltip="Provisioned data transfer rate (MiB/s) compared to actual usage over time."
        testId="throughput-chart"
        unit="MiB/s"
        metrics={{
          used: {
            avg:
              summary?.throughputBytes !== undefined
                ? convertUnit(summary.throughputBytes, 'Bytes', 'MiB')
                : undefined,
            dataKey: 'throughputMiBs',
          },
          provisioned: {
            avg:
              summary?.provisionedThroughputBytes !== undefined
                ? convertUnit(
                    summary.provisionedThroughputBytes,
                    'Bytes',
                    'MiB'
                  )
                : undefined,
            dataKey: 'provisionedThroughputMiBs',
          },
        }}
        labelConfig={labels?.throughput}
        enabledMetrics={enabledMetrics}
        onMetricChange={setEnabledMetrics}
        analyzeWithOpsPilot={analyzeThroughputWithOpsPilot}
      />
    </Stack>
  );
};
